import { sharedShowApi } from '@/api/sharedShowApi'
import { demoWorkspaceApi } from '@/api/demoWorkspaceApi'
import type {
  BookAppointmentInput,
  CreateSaleInput,
  GrantCreditInput,
  PlanActivityInput,
} from '@/features/actions/schemas/actionSchemas'
import { demoCustomers } from '@/features/customers/demo/demoCustomers'
import type { CreateCustomerInput } from '@/features/customers/schemas/customerSchema'
import type {
  Customer,
  CustomerDetail,
  CustomerPage,
  CustomerQuery,
} from '@/features/customers/types'
import type { Credits, PipelineState } from '@/types/models'
import { api } from '@/api/client'
import { ApiError } from '@/api/errors'
import { schedulerApi } from '@/api/schedulerApi'
import { endpoints } from '@/api/endpoints'
import { env } from '@/lib/env'

/**
 * What a nameless customer is called on screen. A lead can arrive with an
 * e-mail only, and every list, card and avatar needs some name to show —
 * the Anrufsliste says the same.
 */
const NO_NAME = 'Ohne Namen'

/** `CustomerCardView.java` — the whole Kundenkarte in one call. */
interface CustomerCardResponse {
  publicId: string
  /** `null` for a lead that arrived with an e-mail only. */
  name: string | null
  firstName: string | null
  lastName: string | null
  email: string | null
  phone: string | null
  /** `null` together with `pipeline`. */
  origin: string | null
  originLabel: string | null
  createdAt: string
  pipeline: PipelineState | null
  credits: Credits
  /** `CustomerAppointmentView.java`. */
  appointments: {
    id: number
    deviceId: number
    deviceName: string
    startsAt: string
    endsAt: string
    kind: string
    kindLabel: string
    status: string
    statusLabel: string
    creditState: string | null
    creditStateLabel: string | null
  }[]
  /** `RecentCallView.java`. */
  recentCalls: {
    id: number
    occurredAt: string
    outcome: string
    outcomeLabel: string
    byUserName: string | null
    note: string | null
  }[]
}

function toCustomerDetail(card: CustomerCardResponse): CustomerDetail {
  return {
    // The wire calls it `publicId`; everything above the api layer says `id`.
    id: card.publicId,
    name: card.name ?? NO_NAME,
    firstName: card.firstName ?? '',
    lastName: card.lastName ?? '',
    email: card.email ?? '',
    phone: card.phone ?? '',
    mobile: card.phone ?? undefined,
    createdAt: card.createdAt,
    origin: card.origin ?? undefined,
    originLabel: card.originLabel ?? undefined,
    pipeline: card.pipeline,
    credits: card.credits,
    // The info card's two counts read the same ledger the Kontingent block shows.
    purchased: card.credits.purchased,
    remaining: card.credits.available,
    appointments: card.appointments.map((appointment) => ({
      ...appointment,
      id: String(appointment.id),
      deviceId: String(appointment.deviceId),
    })),
    recentCalls: card.recentCalls.map((call) => ({ ...call, id: String(call.id) })),
  }
}

/** What `POST /api/customers/{publicId}/credit-grants` returns. */
export interface CreditGrant {
  customerPublicId: string
  granted: {
    quantity: number
    note: string
    grantedAt: string
    byUserName: string
  }
  credits: Credits
  /** Appointments whose credit state changed because of this grant. */
  appointmentsChanged: {
    id: number
    creditState: string
    creditStateLabel: string | null
    previousCreditState: string | null
    previousCreditStateLabel: string | null
  }[]
}

/**
 * One row of `GET /api/customers` — `CustomerListView`, field for field. One
 * endpoint feeds two screens: this list and the CRM-Pipeline board (one
 * request per column, `?stage=`), because a row and a card carry the same
 * fields.
 *
 * Rows arrive in pages, ordered by last name, then first name. That is a
 * decision the server made and the list keeps it — re-sorting a page here
 * would quietly reorder the book under whoever is reading it.
 */
interface CustomerListResponse {
  customerPublicId: string
  /** `null` for a lead that arrived with an e-mail only. */
  name: string | null
  firstName: string | null
  lastName: string | null
  email: string | null
  phone: string | null
  origin: string | null
  originLabel: string | null
  stage: string | null
  stageLabel: string | null
  stageSection: string | null
  attemptCount: number | null
  nextActionAt: string | null
  dueToday: boolean | null
  /**
   * Zeros for a customer who bought nothing. Optional only because a backend
   * built before this field (a VPS not yet redeployed) omits it — the card
   * then shows a dash instead of crashing.
   */
  credits?: Credits
}

/** `CustomerPage.java`. */
interface CustomerPageResponse {
  items: CustomerListResponse[]
  page: number
  size: number
  total: number
}

function toCustomer(row: CustomerListResponse): Customer {
  return {
    // The wire calls it `customerPublicId`; everything above the api layer
    // knows a customer by `id`.
    id: row.customerPublicId,
    name: row.name ?? NO_NAME,
    firstName: row.firstName ?? undefined,
    lastName: row.lastName ?? undefined,
    email: row.email ?? undefined,
    phone: row.phone ?? undefined,
    origin: row.origin ?? undefined,
    originLabel: row.originLabel ?? undefined,
    stage: row.stage ?? undefined,
    stageLabel: row.stageLabel ?? undefined,
    stageSection: row.stageSection ?? undefined,
    attemptCount: row.attemptCount ?? undefined,
    nextActionAt: row.nextActionAt,
    dueToday: row.dueToday ?? undefined,
    // The card's "Termine" is what the ledger still has to spend.
    purchased: row.credits?.purchased,
    remaining: row.credits?.available,
  }
}

export const customersApi = {
  fetchPage: async (query: CustomerQuery): Promise<CustomerPage> => {
    if (env.sharedShow) return sharedShowApi.fetchPage(query)
    if (env.demoMode && !env.complianceDevelopment) return demoCustomers.fetchPage(query)
    if (env.complianceDevelopment) {
      const page = await demoCustomers.fetchPage(query)
      return {
        ...page,
        items: page.items.map((customer) => {
          const credits = demoWorkspaceApi.credits(customer.id)
          return {
            ...customer,
            purchased: credits.purchased,
            remaining: credits.available + credits.reserved,
          }
        }),
      }
    }

    const response = await api.get<CustomerPageResponse>(endpoints.customers.list, {
      // An empty search is "no filter", not "names containing nothing".
      params: { ...query, q: query.q?.trim() || undefined },
    })
    return { ...response, items: response.items.map(toCustomer) }
  },

  /**
   * `GET /api/customers/{publicId}` returns the full Kundenkarte: contact,
   * pipeline (or `null`), credits, appointments and recent calls. The
   * prototype's Odoo fields (treatments, documents, notes…) have no server
   * counterpart and stay absent, so the profile renders a dash for them.
   */
  fetchDetail: async (customerId: string): Promise<CustomerDetail> => {
    if (env.sharedShow) return sharedShowApi.fetchDetail(customerId)
    if (env.demoMode && !env.complianceDevelopment) return demoCustomers.fetchDetail(customerId)
    if (env.complianceDevelopment) {
      const customer = await demoCustomers.fetchDetail(customerId)
      const credits = demoWorkspaceApi.credits(customerId)
      return {
        ...customer,
        credits,
        purchased: credits.purchased,
        remaining: credits.available + credits.reserved,
      }
    }

    return toCustomerDetail(
      await api.get<CustomerCardResponse>(endpoints.customers.detail(customerId)),
    )
  },

  create: (input: CreateCustomerInput) =>
    // NOT ON THE BACKEND YET — there is no customer-create endpoint.
    env.sharedShow ? sharedShowApi.createCustomer(input) : demoCustomers.create(input),

  update: (customerId: string, input: CreateCustomerInput) =>
    // NOT ON THE BACKEND YET — the detail endpoint is read-only.
    env.sharedShow
      ? sharedShowApi.updateCustomer(customerId, input)
      : demoCustomers.update(customerId, input),

  grantCredits: async (customerId: string, input: GrantCreditInput): Promise<CreditGrant> => {
    if (env.sharedShow) return sharedShowApi.grant(customerId, input)
    if (env.complianceDevelopment) return demoWorkspaceApi.grant(customerId, input)
    if (env.demoMode) return demoCustomers.grantCredits(customerId, input)

    const grant = await api.post<CreditGrant>(endpoints.customers.creditGrants(customerId), input)

    // The response echoes who it credited. If that is ever not who we asked
    // about, the contingent on screen belongs to someone else — better to fail
    // loudly than to show one customer's credit under another's name.
    if (grant.customerPublicId !== customerId) {
      throw new ApiError({
        message: 'Die Gutschrift wurde einem anderen Kunden zugeordnet.',
        code: 'CUSTOMER_MISMATCH',
      })
    }

    return grant
  },

  planActivity: (_input: PlanActivityInput) =>
    // NOT ON THE BACKEND YET.
    demoCustomers.recordAction(),

  /**
   * The appointment endpoint is one endpoint. Booking from here goes through
   * the scheduler's mapper so the payload is the backend's shape — every screen
   * shares one booking form, so they must not each invent a request body.
   */
  bookAppointment: (input: BookAppointmentInput, timezone: string) =>
    schedulerApi.create(input, timezone),

  createSale: (_input: CreateSaleInput) =>
    // NOT ON THE BACKEND YET.
    demoCustomers.recordAction(),
}
